import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';

const INTAKE_URL =
  (import.meta.env.VITE_INTAKE_URL as string | undefined) ?? 'http://localhost:5174';

interface EmbedPanelProps {
  formId: string;
  formStatus: 'draft' | 'published' | 'archived';
}

/**
 * Share / Embed panel shown in the form builder's right-side tab.
 * Displays a QR code, direct link, and iframe embed snippet for published forms.
 * Shows a prompt to publish for draft/archived forms.
 */
export function EmbedPanel({ formId, formStatus }: EmbedPanelProps) {
  const publicUrl = `${INTAKE_URL}/f/${formId}`;
  const [copied, setCopied] = useState<'url' | 'embed' | null>(null);

  const embedSnippet = `<iframe
  src="${publicUrl}"
  style="width:100%;border:none;"
  id="develio-form-${formId}"
  title="Intake Form"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'develio:resize') {
    var frame = document.getElementById('develio-form-${formId}');
    if (frame) frame.style.height = e.data.height + 'px';
  }
});
</script>`;

  const copy = async (text: string, which: 'url' | 'embed') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard permission denied */
    }
  };

  if (formStatus !== 'published') {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mb-3 text-slate-300"
          aria-hidden="true"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        <p className="text-sm text-slate-500">
          Publish this form to get the shareable link and embed code.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-y-auto p-5">
      {/* QR Code */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">QR Code</p>
        <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-4">
          <QRCodeSVG value={publicUrl} size={152} level="M" />
        </div>
        <p className="mt-2 break-all text-center text-xs text-slate-400">{publicUrl}</p>
      </div>

      {/* Direct link */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Direct Link
        </p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={publicUrl}
            className="min-w-0 flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 focus:outline-none"
            aria-label="Public form URL"
          />
          <button
            type="button"
            onClick={() => copy(publicUrl, 'url')}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            {copied === 'url' ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 block text-xs text-slate-400 hover:text-slate-700 hover:underline"
        >
          Open in new tab →
        </a>
      </div>

      {/* Embed snippet */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Embed Code
        </p>
        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          {embedSnippet}
        </pre>
        <button
          type="button"
          onClick={() => copy(embedSnippet, 'embed')}
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          {copied === 'embed' ? 'Copied!' : 'Copy embed code'}
        </button>
        <p className="mt-1.5 text-xs text-slate-400">
          Paste into any webpage. The iframe auto-resizes to fit the form content.
        </p>
      </div>
    </div>
  );
}
